import { useContext, useEffect, useState } from 'react';
import IPage from '../../interfaces/IPage';
import { Container } from './MessageTemplate.styled';
import PageTitle from '../../components/page-title/PageTitle';
import IMessageTemplate from '../../interfaces/iMessageTemplate';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMessageTemplateService } from '../../hooks/services/useMessageTemplateService';
import { useToastrVanila } from '../../hooks/util/useToastrVanila';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import IFormError from '../../interfaces/IFormError';
import { MdOutlineAssignmentReturn } from 'react-icons/md';
import { LoadingButton } from '@mui/lab';
import * as yup from 'yup';
import IMessageType from '../../interfaces/IMessageType';
import { useValidateSchema } from '../../hooks/providers/useValidateSchema';
import { hasAccessToAction } from '../../util/hasAccessTo';
import UserContext from '../../context/user/UserContext';

interface IProps extends IPage { }

const initialTemplate: IMessageTemplate = {
  id: 0,
  title: '',
  name: '',
  messageTypeId: 0,
  greeting: '',
  body: '',
  footer: '',
  isDefault: true,
  templateMeta: '',
  messageType: {
    id: 0,
    description: '',
    application: 'EMAIL',
  },
};

const application = {
  WHATSAPP: 'Whatsapp',
  EMAIL: 'E-mail',
};

const MessageTemplateForm = (props: IProps) => {
  window.document.title = props.title;

  const [template, setTemplate] = useState<IMessageTemplate>({ ...initialTemplate });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<IFormError | null>(null);
  const [types, setTypes] = useState<IMessageType[]>([]);
  const [action, setAction] = useState<'edit' | 'new'>('edit');
  const [actionLabel, setActionLabel] = useState('INCLUSÃO');

  const params = useParams();
  const request = useMessageTemplateService();
  const toastr = useToastrVanila();
  const validator = useValidateSchema();
  const navigate = useNavigate();
  const location = useLocation();

  const { state } = useContext(UserContext);
  const { resourcesWithActions } = state;

  const isEdit = hasAccessToAction(
    ["modelos-mensagem-atestado-web"],
    resourcesWithActions,
    "Editar"
  );

  useEffect(() => {
    const id = params.id;
    if (id) {
      loadTemplate(parseInt(id));
      listTypes();
    }
  }, [params.id]);

  const loadTemplate = async (id: number) => {
    setLoading(true);
    await request
      .listById(id)
      .then(result => {
        setTemplate({ ...result });
        setLoading(false);
      })
      .catch(() => {
        toastr.error('Ocorreu um erro ao carregar aos dados da página.');
        setLoading(false);
      });
  };

  const listTypes = async () => {
    setLoading(true);
    await request
      .listTypes()
      .then(result => {
        setTypes([...result]);
        setLoading(false);
      })
      .catch(() => {
        toastr.error('Ocorreu um erro ao carregar aos dados da página.');
        setLoading(false);
      });
  };

  const listTemplateById = async (id: number): Promise<void> => {
    setLoading(true);
    await request
      .listById(id)
      .then(result => {
        setLoading(false);
        setTemplate({ ...result });
      })
      .catch(error => {
        setLoading(false);
        toastr.error(error.message);
      });
  };

  const createTemplate = async (template: IMessageTemplate): Promise<void> => {
    setLoading(true);
    await request
      .create(instanticateTemplate(template))
      .then(() => {
        toastr.success('Documento cadastrado com sucesso!');
        navigate(`/modelo-mensagem`);
        setLoading(false);
      })
      .catch((error) => {
        toastr.error(error?.message || 'Ocorreu um erro ao salvar o documento. Tente mais tarde.');
        setLoading(false);
      });
  };

  const updateTemplate = async (template: IMessageTemplate): Promise<void> => {
    setLoading(true);
    await request
      .update(template.id, instanticateTemplate(template))
      .then(() => {
        toastr.success('Documento atualizado com sucesso!');
        navigate(`/modelo-mensagem`);
        setLoading(false);
      })
      .catch((error) => {
        toastr.error(error?.message || 'Ocorreu um erro ao salvar o documento. Tente mais tarde.');
        setLoading(false);
      });
  };

  const instanticateTemplate = (resource: IMessageTemplate): IMessageTemplate => {
    return {
      id: resource.id,
      name: resource.name,
      title: resource.title,
      messageTypeId: resource.messageTypeId,
      greeting: resource.greeting,
      body: resource.body,
      footer: resource.footer,
      isDefault: resource.isDefault,
      templateMeta: resource.templateMeta || null,
      messageType: {
        id: resource.messageTypeId,
        description: resource.messageType?.description,
        application: resource.messageType?.application,
      },
    };
  };

  const schema = yup.object({
    title: yup.string().required('Campo obrigatório'),
    name: yup.string().required('Campo obrigatório'),
    messageTypeId: yup.number().min(1, 'Campo obrigatório'),
    greeting: yup.string().required('Campo obrigatório'),
    body: yup.string().required('Campo obrigatório'),
    footer: yup.string().required('Campo obrigatório'),
  });

  const handleSave = async () => {
    const resource = { ...template };
    if (!resource.title) {
      resource.title = '';
    }
    const result = await validator.validate(schema, resource);
    if (!result.isValid) {
      setErrors(result.errors);
      return;
    } else {
      setErrors(result.errors);
    }
    if (action === 'edit') {
      updateTemplate({ ...resource }).then(() => {
        navigate(`/modelo-mensagem`);
      });
    } else {
      createTemplate({ ...resource }).then(() => {
        navigate(`/modelo-mensagem`);
      });
    }
  };

  useEffect(() => {
    const action = location.pathname.split('/')[location.pathname.split('/').length - 1] as 'edit' | 'new';
    const id = parseInt(location.pathname.split('/')[location.pathname.split('/').length - 2]);
    setAction(action);
    if (action === 'edit') {
      setActionLabel('EDIÇÃO');
    } else {
      setActionLabel('INCLUSÃO');
    }
    if (id !== 0) {
      listTemplateById(id);
    }
  }, []);

  return (
    <Container>
      <PageTitle title={`${actionLabel} DE MODELO DE MENSAGENS`}>
        <Tooltip title="Voltar para listagem" placement="left-start">
          <IconButton onClick={() => navigate(`/modelo-mensagem`)} color="inherit">
            <MdOutlineAssignmentReturn />
          </IconButton>
        </Tooltip>
      </PageTitle>
      {loading && <LinearProgress />}
      <Grid container spacing={2} sx={{ marginTop: 2 }}>
        <Grid item xs={12} md={12}>
          {types.length > 0 && (
            <FormControl fullWidth error={Boolean(errors?.professionalState)}>
              <InputLabel id="professional-state" shrink>Tipo de Mensagem</InputLabel>
              <Select
                labelId="professional-state"
                id="professional-state-select"
                value={template.messageTypeId || ''}
                label="Tipo de Mensagem"
                notched
                onChange={e => setTemplate({ ...template, messageTypeId: parseInt(`${e.target.value}`) })}
                MenuProps={{ sx: { maxHeight: '300px' } }}
              >
                {types.map((type, i) => {
                  return (
                    <MenuItem value={type.id} key={i}>
                      {`${type.description} - Aplicação: ${application[type.application]}`}
                    </MenuItem>
                  );
                })}
              </Select>
              <FormHelperText>{errors?.professionalState}</FormHelperText>
            </FormControl>
          )}
        </Grid>
        <Grid item xs={12} md={12}>
          <TextField
            fullWidth
            label="Nome do Modelo"
            InputLabelProps={{ shrink: true }}
            placeholder="Informe nome do modelo"
            value={template.name}
            onChange={e => setTemplate({ ...template, name: e.target.value })}
            error={Boolean(errors?.name)}
            helperText={errors?.name}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <TextField
            fullWidth
            label="Template Meta"
            InputLabelProps={{ shrink: true }}
            placeholder="Informe o nome do template cadastrado na Meta"
            value={template.templateMeta || ''}
            onChange={e => setTemplate({ ...template, templateMeta: e.target.value })}
            error={Boolean(errors?.templateMeta)}
            helperText={errors?.templateMeta}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <TextField
            fullWidth
            label="Título do e-mail"
            InputLabelProps={{ shrink: true }}
            placeholder="Informe o título que será utilizado para envio do e-mail"
            value={template.title || ''}
            onChange={e => setTemplate({ ...template, title: e.target.value })}
            error={Boolean(errors?.title)}
            helperText={errors?.title}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <TextField
            fullWidth
            label="Saudação do e-mail ou mensagem de Whatsapp"
            InputLabelProps={{ shrink: true }}
            placeholder="Informe a saudação para ser utilizada no e-mail ou mensagem de Whatsapp"
            value={template.greeting}
            onChange={e => setTemplate({ ...template, greeting: e.target.value })}
            error={Boolean(errors?.greeting)}
            helperText={errors?.greeting}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <TextField
            fullWidth
            label="Corpo do e-mail ou mensagem de Whatsapp"
            InputLabelProps={{ shrink: true }}
            placeholder="Informe o corpo para ser utilizada no e-mail ou mensagem de Whatsapp"
            value={template.body}
            onChange={e => setTemplate({ ...template, body: e.target.value })}
            error={Boolean(errors?.body)}
            helperText={errors?.body}
            multiline
            minRows={4}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <TextField
            fullWidth
            label="Despedida do e-mail ou mensagem de Whatsapp"
            InputLabelProps={{ shrink: true }}
            placeholder="Informe despedida para ser utilizada no e-mail ou mensagem de Whatsapp"
            value={template.footer}
            onChange={e => setTemplate({ ...template, footer: e.target.value })}
            error={Boolean(errors?.footer)}
            helperText={errors?.footer}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <FormControlLabel
            label="Modelo Padrão"
            control={
              <Checkbox
                onChange={e => setTemplate({ ...template, isDefault: e.target.checked })}
                checked={template.isDefault}
                disabled
              />
            }
          />
        </Grid>

        <Grid item>
          <LoadingButton loading={loading} variant="contained" color="primary" onClick={handleSave} disabled={!isEdit}>
            Salvar
          </LoadingButton>
        </Grid>
      </Grid>
    </Container>
  );
};

export default MessageTemplateForm;
